import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { forwardRef } from "react"

import { TransferTypeChip } from "../TransferTypeChip"

import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import { DATE_FORMAT } from "@/constants/formats"
import type { Transfer } from "@/graphql/codegen/graphql"
import { InventoryWorkOrderLocationCell, InventoryWorkOrderPrintLayout } from "@/screens/Inventory/components"
import { formatDate } from "@/utils/date"
import { formatNumber } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface TransferWorkOrderPrintViewProps {
  transfer: Transfer
}

export const TransferWorkOrderPrintView = forwardRef<HTMLDivElement, TransferWorkOrderPrintViewProps>(
  ({ transfer }, ref) => (
    <InventoryWorkOrderPrintLayout
      ref={ref}
      title={`Work Order Slip: ${transfer.referenceCode}`}
      chip={<TransferTypeChip type={transfer.transferType} />}
      workDate={formatDate(transfer.createdOn, DATE_FORMAT.US)}
      warehouseLabel={formatWarehouseLabel(transfer.sourceWarehouseArea)}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Ref #</TableCell>
            <TableCell>Product ID</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Qty to Move</TableCell>
            <TableCell>Current Location</TableCell>
            <TableCell>Destination</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transfer.products.flatMap(({ product, pallets }) =>
            pallets.map((pallet) => {
              const uomName = product.packageUsWeightUomDetails?.name

              return (
                <TableRow className='align-top' key={`${product.productId}-${pallet.referenceNbr}`}>
                  <TableCell>{pallet.referenceNbr}</TableCell>

                  <TableCell>
                    <ProductStatusCell product={product} />
                  </TableCell>

                  <TableCell>
                    <Box className='flex flex-col gap-y-1'>
                      <Box className='font-bold'>{product.companyProductDescription}</Box>
                      <Box>{product.vendorDescription}</Box>
                      {pallet.lots.map((lot) => (
                        <Box key={lot.transferLotId}>
                          Lot/Batch: {lot.rawInventory?.lotNbr}
                          {lot.rawInventory?.batchNbr ? ` / ${lot.rawInventory.batchNbr}` : ""}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box className='flex flex-col gap-y-1 pt-[44px]'>
                      {pallet.lots.map((lot) => (
                        <Box key={lot.transferLotId}>
                          {formatNumber(lot.qtyToMove!, 0, 0)}
                          {uomName ? ` ${uomName}` : ""}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <InventoryWorkOrderLocationCell
                      warehouseShelving={pallet.sourceWarehouseShelving}
                      location={pallet.sourceLocation}
                    />
                  </TableCell>

                  <TableCell>
                    <InventoryWorkOrderLocationCell
                      warehouseShelving={pallet.destinationWarehouseShelving}
                      location={pallet.destinationLocation}
                    />
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </InventoryWorkOrderPrintLayout>
  )
)
